import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import moment from 'moment';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import {
  ticketsPrioritiesColor,
  tasksStatuses
} from '../../utils/constants/tagColors';

const useColumns = () => {
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI } = useAuthContext();
  const [statusList, setStatusList] = useState([]);
  const [priorityList, setPriorityList] = useState([]);

  const getEnum = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/tickets/enums' });
      setStatusList(data.statuses);
      setPriorityList(data.priorities);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const displayAddress = (contract) => {
    if (contract?.type?.includes('MRP')) {
      return `${contract.sites[0].address.postal_code} ${contract.sites[0].address.city}`;
    }
    if (contract?.type === 'MRI') {
      return `${contract.buildings[0].address.postal_code} ${contract.buildings[0].address.city}`;
    }
    return '-';
  };

  useEffect(() => {
    (async () => {
      await getEnum();
    })();
    // eslint-disable-next-line
  }, []);

  return [
    {
      title: t('tickets.form.quote'),
      key: 'contract',
      dataIndex: 'contract',
      sorter: true,
      render: (contract, ticket) =>
        ticket?.contract_number || contract?.number || 'N/R',
      width: 150
    },
    {
      title: t('tickets.form.status'),
      key: 'status',
      dataIndex: 'status',
      sorter: true,
      render: (status) =>
        status && (
          <Tag color={tasksStatuses[status]}>{t(`tickets.tags.${status}`)}</Tag>
        ),
      filters: statusList.map((p) => ({
        text: t(`tickets.tags.${p}`),
        value: p
      })),
      width: 180
    },
    {
      title: t('tickets.form.last_name'),
      key: 'contract',
      dataIndex: 'contract',
      sorter: true,
      render: (contract) =>
        contract?.customer ? `${contract?.customer.last_name}` : 'N/A',
      width: 120
    },
    {
      title: t('tickets.form.first_name'),
      key: 'contract',
      dataIndex: 'contract',
      sorter: true,
      render: (contract) =>
        contract?.customer ? `${contract?.customer.first_name}` : 'N/A',
      width: 150
    },
    {
      title: t('tickets.form.COMPANY'),
      key: 'contract',
      dataIndex: 'contract',
      sorter: true,
      render: (contract) =>
        contract?.customer?.company_name
          ? `${contract.customer.company_name}`
          : '-',
      width: 150
    },
    {
      title: t('tickets.form.address'),
      key: 'contract',
      dataIndex: 'contract',
      sorter: true,
      width: 150,
      render: (contract) => displayAddress(contract)
    },
    {
      title: t('tickets.form.name'),
      key: 'assignee',
      dataIndex: 'assignee',
      sorter: true,
      ellipsis: true,
      render: (user) => user && `${user.name}`,
      width: 125
    },
    {
      title: t('tickets.form.priority'),
      key: 'priority',
      dataIndex: 'priority',
      sorter: true,
      ellipsis: true,
      render: (priority) =>
        priority && (
          <Tag color={ticketsPrioritiesColor[priority]}>
            {t(`tickets.tags.${priority}`)}
          </Tag>
        ),
      filters: priorityList.map((p) => ({
        text: t(`tickets.tags.${p}`),
        value: p
      })),
      width: 180
    },
    {
      title: t('tickets.form.sent'),
      key: 'ticket_messages_sent',
      sorter: true,
      render: (_, record) => {
        const date = record.ticket_messages.filter(
          (messag) =>
            messag?.author.role === 'users:SUPER-USER' ||
            messag?.author.role === 'users:USER'
        );
        if (date.length > 0)
          return moment(date.pop().date).format('DD/MM/YYYY à HH:mm:ss');
        return 'N/A';
      },
      width: 160
    },
    {
      title: t('tickets.form.feedback'),
      key: 'ticket_messages_fe',
      render: (_, record) => {
        const date = record.ticket_messages.filter(
          (messag) =>
            messag?.author.role === 'admins:SUPER-ADMIN' ||
            messag?.author.role === 'admins:ADMIN'
        );
        if (date.length > 0)
          return moment(date.pop().date).format('DD/MM/YYYY à HH:mm:ss');
        return 'N/A';
      },
      sorter: (a, b) => a.date - b.date,

      width: 160
    },
    {
      title: t('tickets.form.time_limit'),
      key: 'delay',
      sorter: true,
      render: (_, record) => {
        const dateFeed = record.ticket_messages.filter(
          (messag) =>
            messag?.author.role === 'admins:SUPER-ADMIN' ||
            messag?.author.role === 'admins:ADMIN'
        );
        const dateSent = record.ticket_messages.filter(
          (messag) =>
            messag?.author.role === 'users:SUPER-USER' ||
            messag?.author.role === 'users:USER'
        );

        const getDifference = (date1, date2) => {
          const ms = moment(new Date(date1)).diff(moment(new Date(date2)));
          const d = moment.duration(ms);
          // eslint-disable-next-line no-underscore-dangle
          return `${d._data.days * 24 + d._data.hours}h${
            // eslint-disable-next-line no-underscore-dangle
            d._data.minutes.toString().length === 1 ? 0 : ''
            // eslint-disable-next-line no-underscore-dangle
          }${d._data.minutes}`;
        };

        if (dateFeed.length > 0 && dateSent.length > 0)
          return `${getDifference(
            dateFeed?.pop()?.date,
            dateSent?.pop()?.date
          )}`;
        return 'N/A';
      },
      width: 80
    },
    {
      title: t('tickets.form.compensation'),
      key: 'contract',
      dataIndex: 'contract',
      sorter: true,
      render: (contract) => `${contract?.remuneration || 0} €`,
      width: 135
    },
    {
      title: t('tickets.form.user'),
      key: 'assignee',
      dataIndex: 'assignee',
      sorter: true,
      render: (user) =>
        user && `${user.owner_first_name} ${user.owner_last_name}`,
      width: 125
    }
  ];
};

export default useColumns;
